// =============================================================================
// Checkout — public, recruit-facing
// =============================================================================
// Loads pending_enrollment by token, renders package, button → Stripe Checkout
// =============================================================================

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function Checkout() {
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/api/checkout/${token}`)
      .then(setPkg)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function startPayment() {
    setPaying(true);
    setError(null);
    try {
      const { url } = await api.post(`/api/checkout/${token}/session`, {});
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setPaying(false);
    }
  }

  if (loading) {
    return <CheckoutShell><div className="text-center text-silver-mute">Loading…</div></CheckoutShell>;
  }
  if (error || !pkg) {
    return <CheckoutShell>
      <div className="text-center">
        <h2 className="text-xl font-display font-bold text-hunter mb-2">We couldn't load this checkout</h2>
        <p className="text-silver-mute">{error || 'Link not found.'}</p>
        <p className="text-sm text-silver-mute mt-4">Contact your CCF representative if you need a new link.</p>
      </div>
    </CheckoutShell>;
  }

  return (
    <CheckoutShell>
      <div className="text-center mb-6">
        <p className="text-xs font-bold tracking-widest uppercase text-mid-green mb-2">Your CCF Plan</p>
        <h1 className="text-3xl font-display font-black text-hunter">
          Welcome, {pkg.recruit_first_name}.
        </h1>
        <p className="text-silver-mute mt-2">Review your custom recruiting plan below.</p>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
        <table className="w-full text-sm">
          <tbody>
            {pkg.package_items.map((item, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-b-0">
                <td className="px-4 py-3">
                  <div className="font-bold">{item.service_name}</div>
                  <div className="text-xs text-silver-mute capitalize">{item.category.replace(/_/g, ' ')}</div>
                </td>
                <td className="px-4 py-3 text-right font-display font-bold text-hunter">
                  ${Number(item.line_total).toLocaleString()}
                </td>
              </tr>
            ))}
            <tr className="bg-silver-a">
              <td className="px-4 py-3 font-display font-bold">Total per year</td>
              <td className="px-4 py-3 text-right font-display font-black text-hunter text-lg">
                ${Number(pkg.total_amount).toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button onClick={startPayment} disabled={paying} className="btn-primary w-full text-base py-3">
        {paying ? 'Redirecting to secure payment…' : 'Complete Enrollment'}
      </button>

      <p className="text-xs text-silver-mute text-center mt-4">
        Payment processed securely by Stripe. This link expires {new Date(pkg.expires_at).toLocaleDateString()}.
      </p>
    </CheckoutShell>
  );
}

function CheckoutShell({ children }) {
  return (
    <div className="min-h-screen bg-hunter-deep flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <h1 className="text-white text-2xl font-display font-black tracking-tight">Coach Cole Football</h1>
          <p className="text-mid-green text-xs tracking-[0.3em] uppercase font-bold mt-1">Recruiting & NIL Services</p>
        </div>
        <div className="card">{children}</div>
      </div>
    </div>
  );
}
